import React from "react";

import { Check } from "lucide-react";

import LogoWithIcon from "@/components/LogoWithIcon";
import SectionTag from "@/components/seo/SectionTag";
import { cn } from "@/lib/utils";

const CompetitorComparisonTable = ({ city, industry }: { city?: string; industry: string }) => {
  const title = city ? `${city} ${industry} Web Design: Us vs Them` : `${industry} Web Design: Us vs Them`;
  return (
    <section className="bg-ash-100 w-full py-24">
      <div className="flex justify-center container mx-auto">
        <div className="max-w-3xl w-full text-center px-4">
          <SectionTag text="Why Choose Rebolt" className="mx-auto" />
          <h2 className="text-3xl lg:text-4xl text-ash-800 font-semibold mt-4 mb-10 px-14">{title}</h2>

          <div className="bg-white shadow-[0px_4px_20px_0px_rgba(0,0,0,0.07)] rounded-[20px] overflow-hidden p-6">
            <div role="grid" aria-labelledby="table-caption" className="grid grid-cols-4 gap-4">
              <div role="row" className="contents">
                <div id="table-caption" className="sr-only">
                  Comparison of Rebolt, Wix/Squarespace, and Agencies for {industry} websites
                </div>

                <div role="columnheader" className="col-span-1" />
                <div role="columnheader" className="font-semibold flex items-center justify-center">
                  <LogoWithIcon fill="#000000" className="lg:ml-4 w-24 lg:w-32 h-10" />
                </div>
                <div
                  role="columnheader"
                  className="font-semibold text-sm lg:text-base flex items-center justify-center"
                >
                  Wix&#8203;/&#8203;Squarespace
                </div>
                <div
                  role="columnheader"
                  className="font-semibold text-sm lg:text-base flex items-center justify-center"
                >
                  Agencies
                </div>
              </div>

              {[
                { feature: "SEO Optimized", builtRight: true, wix: false, agencies: true },
                { feature: "Affordable", builtRight: true, wix: true, agencies: false },
                { feature: "Ready in < 24 hours", builtRight: true, wix: true, agencies: false },
                { feature: "CRO Optimized", builtRight: true, wix: false, agencies: false },
                { feature: "White glove support", builtRight: true, wix: false, agencies: false },
                { feature: "Long-term contracts", builtRight: false, wix: false, agencies: true },
              ].map((row) => (
                <React.Fragment key={row.feature}>
                  <div role="row" className="contents">
                    <div className="col-span-4 grid grid-cols-4 bg-ash-100 rounded-[20px] py-2.5 items-center">
                      <div role="rowheader" className="font-semibold text-left text-xs md:text-sm px-4">
                        {row.feature}
                      </div>
                      {[row.builtRight, row.wix, row.agencies].map((hasFeature, cellIndex) => (
                        <div role="gridcell" key={cellIndex} className="flex justify-center">
                          {hasFeature && (
                            <span
                              role="img"
                              aria-hidden="true"
                              className={cn(
                                "flex items-center justify-center h-fit",
                                cellIndex === 0 ? "bg-black rounded-full p-1" : ""
                              )}
                            >
                              <Check className={cn(cellIndex === 0 ? "h-3 w-3 text-white" : "h-4 w-4")} />
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompetitorComparisonTable;
